import 'normalize.css';
import '@shared/styles/vars.css';
import '@shared/styles/lighthouse.css';
import './index.css';
import '@shared/styles/print.css';

import { ApolloClient, ApolloLink, ApolloProvider } from '@apollo/client';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { setSharedApolloClient } from '@shared/api/client';
import { MBToastProvider } from '@shared/components/toast';
import { UpgradeModalContextProvider } from '@shared/scenes/billing/upgradeModal/context';
import { Provider, configureStore } from '@shared/store';
import { useDispatchViewportDimensions } from '@shared/store/useDispatchViewportDimensions';
import { GoogleMapsProvider } from '@shared/util/googleMaps';
import { PrintProvider } from '@shared/util/print/PrintProvider';
import { MBSentry } from '@shared/util/sentry';
import { parseUserIdFromToken } from '@shared/util/token';
import { UIProvider } from '@ui/UIProvider';
import { createApolloClient } from './Client';
import { ExternalContent } from './ExternalContent';
import { ExternalPermissionError } from './ExternalPermissionError';
import { useInitLaunchDarkly } from './hooks';

function Hooks() {
  useDispatchViewportDimensions();
  return null;
}

const safeAreaInsets = { top: 0, left: 0, right: 0, bottom: 0 };
const noopUpgradeModal = { show: () => {} };

export function App() {
  const params = new URLSearchParams(window.location.search);
  const templateSlug: string | null = getTemplateSlug(params);
  const businessId = Number(params.get('businessId'));
  const channelId = Number(params.get('channelId'));
  const cardId = params.get('cardId');
  const authToken = params.get('authToken');

  const [store, setStore] = useState<MBStore>();
  const [client, setClient] = useState<ApolloClient<any>>();

  useInitLaunchDarkly(businessId);

  useEffect(() => {
    const init = async () => {
      MBSentry.init({ dsn: import.meta.env.VITE_EXTERNAL_SENTRY_DSN });
      const newStore = configureStore();
      const newClient = createApolloClient([
        new ApolloLink((operation, forward) => {
          operation.setContext({
            headers: {
              // Note(fant): for legacy reasons, this is "WEB" | "IOS" | "ANDROID"
              'x-ts-platform': 'WEB',
              'x-coast-business-id': businessId,
              authorization: `Bearer ${authToken}`,
            },
          });
          return forward(operation);
        }),
      ]);

      setStore(newStore);
      setClient(newClient);
      setSharedApolloClient(newClient);
      setExternalUser(authToken, newStore.dispatch);
    };

    init();
  }, [authToken, businessId]);

  if (!store || !client) {
    return null;
  }

  if (!businessId || !channelId || !authToken) {
    return (
      <Provider store={store}>
        <ExternalPermissionError
          businessId={businessId}
          channelId={channelId}
          hasAuthToken={!isNil(authToken)}
        />
      </Provider>
    );
  }

  return (
    <UIProvider>
      <SafeAreaInsetsContext.Provider value={safeAreaInsets}>
        <ApolloProvider client={client}>
          <GoogleMapsProvider>
            <Provider store={store}>
              <MBToastProvider>
                <UpgradeModalContextProvider value={noopUpgradeModal}>
                  <PrintProvider>
                    <ExternalContent
                      cardId={cardId}
                      channelId={channelId}
                      templateSlug={templateSlug ?? undefined}
                    />
                    <Hooks />
                  </PrintProvider>
                </UpgradeModalContextProvider>
              </MBToastProvider>
            </Provider>
          </GoogleMapsProvider>
        </ApolloProvider>
      </SafeAreaInsetsContext.Provider>
    </UIProvider>
  );
}

/**
 * viewTemplateId maps to templateSlug. Public card links and create forms
 * require viewTemplateId; ExternalContent shows an error when it is missing.
 */
function getTemplateSlug(params: URLSearchParams): string | null {
  return params.get('viewTemplateId');
}

/**
 * Marks the session as external and seeds the anonymous user id (parsed from the
 * URL auth token) into the store. The isExternal flag lets shared hooks skip
 * authenticated-only queries (e.g. getCurrentUser in useProfile) while still
 * exposing the user id for things like geolocation attribution.
 */
function setExternalUser(authToken: string | null, dispatch: MBDispatch) {
  dispatch({ type: 'SET_IS_EXTERNAL', value: { isExternal: true } });
  if (!isNil(authToken)) {
    const userIdFromUrlToken = parseUserIdFromToken(authToken);
    if (!isNil(userIdFromUrlToken)) {
      dispatch({ type: 'SET_USER_ID', value: { id: userIdFromUrlToken } });
    }
  }
}
